import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItemText,
  ListItemButton,
  Divider,
  useTheme,
  Drawer,
  Paper,
  alpha,
} from '@mui/material';
import { RIGHT_DRAWER_WIDTH } from '../types';

interface RoiWorkRightPanelProps {
  rightDrawerOpen: boolean;
  selectedRoiId: string;
  setSelectedRoiId: (id: string) => void;
  roiData: any;
}

export const RoiWorkRightPanel: React.FC<RoiWorkRightPanelProps> = ({
  rightDrawerOpen,
  selectedRoiId,
  setSelectedRoiId,
  roiData,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Drawer
      sx={{
        width: RIGHT_DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: RIGHT_DRAWER_WIDTH,
          bgcolor: 'background.paper',
          borderLeft: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
          height: '100%',
          position: 'relative'
        },
      }}
      variant="persistent"
      anchor="right"
      open={rightDrawerOpen}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: isDark ? alpha(theme.palette.info.main, 0.05) : alpha(theme.palette.info.main, 0.03),
          borderRadius: 0
        }}
      >
        <Typography variant="button" sx={{
          fontWeight: 700,
          color: 'info.main',
          letterSpacing: 1
        }}>
          PROPERTIES
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', flex: 1 }}>
        <Box sx={{ p: 2 }}>
          {selectedRoiId ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase'
                }}>
                  ID
                </Typography>
                <Typography variant="h6" sx={{
                  color: 'text.primary',
                  fontWeight: 700,
                  letterSpacing: '-0.01em'
                }}>
                  {selectedRoiId}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="body2" sx={{
              color: 'text.disabled',
              fontStyle: 'italic'
            }}>
              Select an ROI to view details.
            </Typography>
          )}
        </Box>
        <Divider />
        <List dense>
          {Object.keys(roiData?.rois || {}).map(id => (
            <ListItemButton
              key={id}
              selected={selectedRoiId === id}
              onClick={() => setSelectedRoiId(id)}
              className={selectedRoiId === id ? 'animate-fade-in' : ''}
              sx={{
                borderLeft: selectedRoiId === id ? `3px solid ${theme.palette.info.main}` : '3px solid transparent',
                transition: 'all 0.2s ease',
                bgcolor: selectedRoiId === id ? alpha(theme.palette.info.main, 0.1) : 'transparent',
                '&:hover': {
                  bgcolor: selectedRoiId === id
                    ? alpha(theme.palette.info.main, 0.15)
                    : alpha(theme.palette.action.hover, 0.05)
                }
              }}
            >
              <ListItemText
                primary={id}
                secondary="Polygon"
                primaryTypographyProps={{
                  color: selectedRoiId === id ? 'info.main' : 'text.primary',
                  fontWeight: selectedRoiId === id ? 600 : 400,
                  fontSize: '0.875rem'
                }}
                secondaryTypographyProps={{
                  color: 'text.secondary',
                  fontSize: '0.75rem'
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};
